import {
  type CarteraCobranzaRespuesta,
  type ColumnaKanban,
  type ConfiguracionEmpresaDto,
  type ConsolaCalendarioRespuesta,
  type ConsolaCostosRespuesta,
  type ConsolaMotorRespuesta,
  type EventoCalendario,
  type FacturaVencidaDetalle,
  type FiltrosCalendario,
  type FiltrosCompetencia,
  type FiltrosPipeline,
  type ItemAuditoriaConfiguracionDto,
  type KpisCalendario,
  type KpisCarteraCobranza,
  type MesPerspectiva,
  type ParametrosPredictorDto,
  type PerfilCompetidor,
  type PerspectivaMultimesEntrada,
  type PerspectivaMultimesRespuesta,
  type PipelineRespuesta,
  type SimularOfertaCompetidorInput,
  type TarjetaKanban,
  type TipoOrganismo,
  type TopDeudorCobranza,
  type TotalesLibroGastos,
  type TotalesListadoFacturas,
  type TramoAgingCobranza,
  type UrgenciaLicitacion,
  type UsuarioEmpresaDto,
  type ValidacionHistoricaRespuesta,
  type CapaCalendario,
  type AvisoUrgenteCalendario,
  type ResumenGeneralRespuesta,
  type RadarFocoInmediato,
  type PeriodoDashboard,
  type FiltrosDashboard,
  type InformeEjecutivoRespuesta,
  type AlertaLicitacionUrgente,
  type AlertaProyectoRiesgo,
  type AlertaFacturaMora,
  type ConsolaCostosServiciosRespuesta,
  type KpisCostosServicios,
  type ComparativoPlanVsRealCategoria,
  type ProyectoConsumoPresupuesto,
  type ConsolaRrhhGastosEmpresaRespuesta,
  type KpisRrhhGastosEmpresa,
  type RemuneracionItem,
  type GastoFijoRecurrenteItem,
  type DetalleInstitucionPrevired,
  type PlanillaPreviredRespuesta,
  type FiltrosRemuneracionInput,
  type RespuestaPaginada,
  CAPAS_CALENDARIO,
} from '@lexicore/shared';

export type {
  CarteraCobranzaRespuesta,
  FacturaVencidaDetalle,
  KpisCarteraCobranza,
  TopDeudorCobranza,
  TramoAgingCobranza,
  TotalesListadoFacturas,
  ConsolaCalendarioRespuesta,
  PerspectivaMultimesRespuesta,
  KpisCalendario,
  CapaCalendario,
  FiltrosCalendario,
  PerspectivaMultimesEntrada,
  EventoCalendario,
  AvisoUrgenteCalendario,
  MesPerspectiva,
  ResumenGeneralRespuesta,
  RadarFocoInmediato,
  PeriodoDashboard,
  FiltrosDashboard,
  InformeEjecutivoRespuesta,
  AlertaLicitacionUrgente,
  AlertaProyectoRiesgo,
  AlertaFacturaMora,
  ConsolaCostosServiciosRespuesta,
  KpisCostosServicios,
  ComparativoPlanVsRealCategoria,
  ProyectoConsumoPresupuesto,
  ConsolaRrhhGastosEmpresaRespuesta,
  KpisRrhhGastosEmpresa,
  RemuneracionItem,
  GastoFijoRecurrenteItem,
  DetalleInstitucionPrevired,
  PlanillaPreviredRespuesta,
  FiltrosRemuneracionInput,
};
export { CAPAS_CALENDARIO };
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, almacenSesion } from './api';

/**
 * Consultas a la API.
 *
 * Las claves son jerárquicas (`['proyectos']`, `['proyectos', id]`) para poder
 * invalidar una rama entera tras una mutación sin enumerar cada consulta. Es lo
 * que hace que registrar un gasto refresque a la vez la ficha del proyecto, el
 * libro de gastos y los KPIs del tablero — sin que ninguno de los tres tenga
 * que saber del otro.
 */

export const claves = {
  analitica: ['analitica'] as const,
  licitaciones: ['licitaciones'] as const,
  proyectos: ['proyectos'] as const,
  gastos: ['gastos'] as const,
  facturas: ['facturas'] as const,
  catalogos: ['catalogos'] as const,
  organismos: ['organismos'] as const,
  calendario: ['calendario'] as const,
  prediccion: ['prediccion'] as const,
  configuracion: ['configuracion'] as const,
  competencia: ['competencia'] as const,
  rrhh: ['rrhh'] as const,
};

// ─────────────────────────────────────────────────────────────
// Analítica
// ─────────────────────────────────────────────────────────────

export type ResumenGeneral = ResumenGeneralRespuesta;

export const useResumenGeneral = (
  filtros?: Partial<FiltrosDashboard> | PeriodoDashboard,
) => {
  const params = new URLSearchParams();
  if (typeof filtros === 'string') {
    params.set('periodo', filtros);
  } else if (filtros) {
    if (filtros.periodo) params.set('periodo', filtros.periodo);
    if (filtros.desde) params.set('desde', filtros.desde);
    if (filtros.hasta) params.set('hasta', filtros.hasta);
  }
  const queryStr = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [...claves.analitica, 'general', filtros ?? '12_meses'],
    queryFn: () =>
      api<ResumenGeneralRespuesta>(`/analitica/general${queryStr}`),
  });
};

export const useInformeEjecutivo = (
  filtros?: Partial<FiltrosDashboard> | PeriodoDashboard,
) => {
  const params = new URLSearchParams();
  if (typeof filtros === 'string') {
    params.set('periodo', filtros);
  } else if (filtros) {
    if (filtros.periodo) params.set('periodo', filtros.periodo);
    if (filtros.desde) params.set('desde', filtros.desde);
    if (filtros.hasta) params.set('hasta', filtros.hasta);
  }
  const queryStr = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [...claves.analitica, 'informe-ejecutivo', filtros ?? '12_meses'],
    queryFn: () =>
      api<InformeEjecutivoRespuesta>(`/analitica/informe-ejecutivo${queryStr}`),
  });
};

export const useRadarFoco = () =>
  useQuery({
    queryKey: [...claves.analitica, 'radar-foco'],
    queryFn: () => api<RadarFocoInmediato>('/analitica/radar-foco'),
  });

export interface PuntoSerie {
  mes: string;
  adjudicado: number;
  costos: number;
  cobrado: number;
  resultado: number;
}

export const useSerieMensual = (meses = 12) =>
  useQuery({
    queryKey: [...claves.analitica, 'serie', meses],
    queryFn: () => api<PuntoSerie[]>(`/analitica/serie-mensual?meses=${meses}`),
  });

export interface DesempenoCategoria {
  id: string;
  nombre: string;
  color: string;
  margenObjetivoPct: number;
  total: number;
  adjudicadas: number;
  perdidas: number;
  winRatePct: number | null;
  ingresos: number;
  costos: number;
  utilidad: number;
  margenRealPct: number | null;
  ratioAdjudicacionPromedio: number | null;
}

export const useDesempenoCategorias = () =>
  useQuery({
    queryKey: [...claves.analitica, 'categorias'],
    queryFn: () => api<DesempenoCategoria[]>('/analitica/categorias'),
  });

export interface MesFlujo {
  mes: string;
  ingresos: number;
  egresos: number;
  flujoNeto: number;
  saldoAcumulado: number;
  ingresosEstimados: number;
}

export const useFlujoCaja = (meses = 6) =>
  useQuery({
    queryKey: [...claves.analitica, 'flujo', meses],
    queryFn: () =>
      api<{ meses: MesFlujo[]; totalIngresosProyectados: number; totalEgresosProyectados: number }>(
        `/analitica/flujo-caja?meses=${meses}`,
      ),
  });

export interface ResumenCompetencia {
  derrotas: number;
  dineroPerdido: number;
  brechaPrecioPromedioPct: number | null;
  brechaPlazoPromedioDias: number | null;
  rivales: { id: string; nombre: string; victorias: number; montoGanado: number; brechaPromedioPct: number | null }[];
  motivosPerdida: { motivo: string; cantidad: number }[];
}

export const useCompetencia = () =>
  useQuery({
    queryKey: [...claves.analitica, 'competencia'],
    queryFn: () => api<ResumenCompetencia>('/analitica/competencia'),
  });

// ─────────────────────────────────────────────────────────────
// Calendario y avisos
// ─────────────────────────────────────────────────────────────

export const useConsolaCalendario = (filtros: Partial<FiltrosCalendario> = {}) =>
  useQuery({
    queryKey: [...claves.calendario, 'consola', filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filtros.mes) params.set('mes', filtros.mes);
      if (filtros.desde) params.set('desde', filtros.desde);
      if (filtros.hasta) params.set('hasta', filtros.hasta);
      if (filtros.capa && filtros.capa !== 'TODOS') params.set('capa', filtros.capa);
      return api<ConsolaCalendarioRespuesta>(`/calendario?${params}`);
    },
  });

export const usePerspectivaMultimes = (entrada: Partial<PerspectivaMultimesEntrada> = {}) =>
  useQuery({
    queryKey: [...claves.calendario, 'perspectiva', entrada],
    queryFn: () => {
      const params = new URLSearchParams();
      if (entrada.mesReferencia) params.set('mesReferencia', entrada.mesReferencia);
      if (entrada.cantidadMeses) params.set('cantidadMeses', String(entrada.cantidadMeses));
      if (entrada.capa && entrada.capa !== 'TODOS') params.set('capa', entrada.capa);
      return api<PerspectivaMultimesRespuesta>(`/calendario/perspectiva?${params}`);
    },
  });

export const useEventos = (desde?: string, hasta?: string, capa?: CapaCalendario) =>
  useQuery({
    queryKey: [...claves.calendario, 'eventos', desde, hasta, capa],
    queryFn: () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      if (capa && capa !== 'TODOS') params.set('capa', capa);
      return api<EventoCalendario[]>(`/calendario/eventos?${params}`);
    },
  });

export interface Avisos {
  total: number;
  criticos: number;
  avisos: { nivel: 'critico' | 'advertencia' | 'informativo'; icono: string; texto: string; entidad: string; entidadId: string }[];
}

export const useAvisosSistema = () =>
  useQuery({
    queryKey: ['notificaciones'],
    queryFn: () => api<Avisos>('/notificaciones'),
    // Se refresca solo cada 2 minutos: los vencimientos cambian con el día, no
    // con el segundo, y consultarlo más seguido sería ruido.
    refetchInterval: 120_000,
    enabled: !!almacenSesion.acceso(),
  });

// ─────────────────────────────────────────────────────────────
// Licitaciones
// ─────────────────────────────────────────────────────────────

export interface Licitacion {
  id: string;
  codigoMercadoPublico: string;
  nombre: string;
  estado: string;
  tipoProceso: string;
  prioridad?: string;
  modalidadMonto?: string;
  presupuestoOficialNeto: number;
  presupuestoOficialBruto?: number;
  montoOfertadoNeto: number;
  montoOfertadoBruto?: number;
  montoAdjudicadoNeto: number;
  montoAdjudicadoBruto?: number;
  fechaCierrePostulacion: string | null;
  fechaAdjudicacion: string | null;
  duracionMeses: number;
  plazoEntregaOfertadoDias: number;
  cantidadOferentes: number | null;
  organismo: { id: string; nombre: string; tipo: string };
  categoria: { id: string; nombre: string; color: string };
  proyecto: { id: string; codigo: string; estado: string } | null;
  ofertasCompetidores: { montoNeto: number; plazoDias: number | null; esGanador: boolean; competidor: { id: string; nombre: string } }[];
  metricas: { brechaPrecioPct: number | null; brechaPlazoDias: number | null; ratioOferta: number | null; esPipelineAbierto: boolean };
}

export interface Pagina<T> {
  datos: T[];
  total: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

export const useLicitaciones = (filtros: Record<string, string | number | undefined> = {}) =>
  useQuery({
    queryKey: [...claves.licitaciones, filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [clave, valor] of Object.entries(filtros)) {
        if (valor !== undefined && valor !== '') params.set(clave, String(valor));
      }
      return api<Pagina<Licitacion>>(`/licitaciones?${params}`);
    },
  });

export type {
  ColumnaKanban,
  FiltrosPipeline,
  PipelineRespuesta,
  TarjetaKanban,
  UrgenciaLicitacion,
};

export const usePipeline = (filtros: FiltrosPipeline = {}) =>
  useQuery({
    queryKey: [...claves.licitaciones, 'pipeline', filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filtros.busqueda) params.set('busqueda', filtros.busqueda);
      if (filtros.sector) params.set('sector', filtros.sector);
      if (filtros.categoriaId) params.set('categoriaId', filtros.categoriaId);
      if (filtros.responsableId) params.set('responsableId', filtros.responsableId);
      if (filtros.soloVencimientoProximo !== undefined && filtros.soloVencimientoProximo !== false) {
        params.set('soloVencimientoProximo', 'true');
      }
      return api<PipelineRespuesta>(`/licitaciones/pipeline?${params}`);
    },
  });


// ─────────────────────────────────────────────────────────────
// Proyectos
// ─────────────────────────────────────────────────────────────

export type SaludProyecto = 'SANO' | 'ALERTA' | 'CRITICO';

export interface MetricasProyecto {
  avancePct: number;
  diasRestantes: number | null;
  costoReal: number;
  utilidad: number;
  margenPct: number | null;
  consumoPresupuestoPct: number;
  hitosCompletados: number;
  hitosTotales: number;
  salud?: SaludProyecto;
  motivosSalud?: string[];
  alertas: { nivel: 'advertencia' | 'critico'; texto: string }[];
}

export interface Proyecto {
  id: string;
  codigo: string;
  nombre: string;
  estado: string;
  modalidadMonto?: string;
  montoContratoNeto: number;
  montoContratoBruto?: number;
  presupuestoCostoPlanificado: number;
  presupuestoCostoPlanificadoBruto?: number;
  fechaInicio: string;
  fechaFinPlanificada: string | null;
  fechaFinReal: string | null;
  modoFacturacion: string;
  organismo?: { id: string; nombre: string; tipo?: string } | null;
  licitacion: { codigoMercadoPublico: string; organismo: { id: string; nombre: string }; categoria: { id: string; nombre: string; color: string } } | null;
  responsable: { id: string; nombre: string } | null;
  metricas: MetricasProyecto;
}

export interface DesviacionProyecto {
  id: string;
  codigo: string;
  nombre: string;
  salud: SaludProyecto;
  consumoPresupuestoPct: number;
  diasRestantes: number | null;
  motivoPrincipal: string;
}

export interface EstadisticasCarteraProyectos {
  totalProyectos: number;
  porEstado: {
    planificados: number;
    enEjecucion: number;
    enPausa: number;
    cerrados: number;
    cancelados: number;
  };
  porSalud: {
    sanos: number;
    alerta: number;
    criticos: number;
  };
  financiero: {
    valorCarteraContratadoNeto: number;
    presupuestoTotalCostosNeto: number;
    costoRealEjecutadoNeto: number;
    utilidadRealAcumuladaNeto: number;
    margenRealPonderadoPct: number;
    totalFacturadoNeto: number;
    totalCobradoNeto: number;
    totalPorCobrarNeto: number;
  };
  proyectosEnRiesgoCount: number;
  topDesviaciones: DesviacionProyecto[];
}

export const useEstadisticasCartera = () =>
  useQuery({
    queryKey: [...claves.proyectos, 'estadisticas'],
    queryFn: () => api<EstadisticasCarteraProyectos>('/proyectos/estadisticas'),
    staleTime: 1000 * 60 * 2,
  });

export interface FiltrosProyectoUI {
  pagina?: number;
  porPagina?: number;
  busqueda?: string;
  estado?: string;
  salud?: string;
  organismoId?: string;
  clienteId?: string;
  categoriaId?: string;
  fechaFinDesde?: string;
  fechaFinHasta?: string;
  soloEnRiesgo?: boolean;
  ordenarPor?: string;
  orden?: 'asc' | 'desc';
}

export interface RespuestaProyectosPaginada {
  datos: Proyecto[];
  total: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
  resumenRapido?: {
    mostradosEnPagina: number;
    totalEnBD: number;
  };
}

export const useProyectosPaginados = (filtros: FiltrosProyectoUI = {}) =>
  useQuery({
    queryKey: [...claves.proyectos, 'paginados', filtros],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filtros.pagina) params.set('pagina', String(filtros.pagina));
      if (filtros.porPagina) params.set('porPagina', String(filtros.porPagina));
      if (filtros.busqueda) params.set('busqueda', filtros.busqueda);
      if (filtros.estado && filtros.estado !== 'TODOS') params.set('estado', filtros.estado);
      if (filtros.salud && filtros.salud !== 'TODOS') params.set('salud', filtros.salud);
      const orgId = filtros.organismoId || filtros.clienteId;
      if (orgId && orgId !== 'TODOS') params.set('organismoId', orgId);
      if (filtros.fechaFinDesde) params.set('fechaFinDesde', filtros.fechaFinDesde);
      if (filtros.fechaFinHasta) params.set('fechaFinHasta', filtros.fechaFinHasta);
      if (filtros.soloEnRiesgo) params.set('soloEnRiesgo', 'true');
      if (filtros.ordenarPor) params.set('ordenarPor', filtros.ordenarPor);
      if (filtros.orden) params.set('orden', filtros.orden);

      const res = await api<RespuestaProyectosPaginada | Proyecto[]>(`/proyectos?${params}`);
      if (Array.isArray(res)) {
        return {
          datos: res,
          total: res.length,
          pagina: 1,
          porPagina: res.length,
          totalPaginas: 1,
        };
      }
      return res;
    },
  });

export const useProyectos = (filtros: FiltrosProyectoUI = {}) =>
  useQuery({
    queryKey: [...claves.proyectos, filtros],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filtros.estado && filtros.estado !== 'TODOS') params.set('estado', filtros.estado);
      if (filtros.busqueda) params.set('busqueda', filtros.busqueda);
      if (filtros.soloEnRiesgo) params.set('soloEnRiesgo', 'true');
      if (filtros.salud && filtros.salud !== 'TODOS') params.set('salud', filtros.salud);
      params.set('porPagina', '150'); // Lista completa para vistas consolidadas

      const res = await api<RespuestaProyectosPaginada | Proyecto[]>(`/proyectos?${params}`);
      return Array.isArray(res) ? res : res.datos ?? [];
    },
  });

export interface LineaDesviacion {
  categoriaGastoId: string;
  categoriaNombre: string;
  planificado: number;
  real: number;
  desviacion: number;
  desviacionPct: number | null;
  consumoPct: number | null;
  fueraDePresupuesto: boolean;
}

export interface ProyectoDetalle extends Proyecto {
  analisisCostos: {
    totalPlanificado: number;
    totalReal: number;
    desviacion: number;
    desviacionPct: number | null;
    consumoPct: number | null;
    porCategoria: LineaDesviacion[];
    estado: 'ok' | 'advertencia' | 'excedido';
  };
  sobrecosto: { avanceTemporal: number; avanceGasto: number; costoFinalEstimado: number | null; sobrecostoEstimado: number | null; enRiesgo: boolean } | null;

  /** A quién se le factura. Puede faltar en proyectos creados sin licitación. */
  organismo: { id: string; nombre: string; tipo: string } | null;

  descripcion: string | null;
  numeroOrdenCompra: string | null;
  fechaOrdenCompra: string | null;
  notas: string | null;

  hitos: Hito[];
  itemsCierre: ItemCierre[];
  notasBitacora: NotaBitacora[];
  lineasPresupuesto: LineaPresupuesto[];
  gastos: GastoProyecto[];
  cuotas: Cuota[];
  /**
   * Las facturas llegan con sus pagos pero SIN la situación de cobro derivada
   * — a diferencia de `/facturas`, que sí la calcula. La ficha la deriva en el
   * cliente con `evaluarFactura` de @lexicore/shared: es el mismo motor que usa
   * el servidor, así que la regla no se duplica.
   */
  facturas: FacturaProyecto[];
}

export interface Hito {
  id: string;
  nombre: string;
  descripcion: string | null;
  fechaPlanificada: string | null;
  fechaReal: string | null;
  completado: boolean;
  esFacturable: boolean;
  montoFacturable: number | null;
  orden: number;
}

export interface ItemCierre {
  id: string;
  texto: string;
  completado: boolean;
  completadoEn: string | null;
}

export interface NotaBitacora {
  id: string;
  texto: string;
  creadoEn: string;
  autor: { id: string; nombre: string } | null;
}

export interface LineaPresupuesto {
  id: string;
  categoriaGastoId: string;
  montoPlanificado: number;
  categoriaGasto: { id: string; nombre: string; tipo: string };
}

export interface GastoProyecto {
  id: string;
  fecha: string;
  concepto: string;
  montoNeto: number;
  ivaPct: number;
  montoIva: number;
  montoTotal: number;
  tipoDocumento: string;
  numeroDocumento: string | null;
  estadoPago: string;
  fechaPago: string | null;
  notas: string | null;
  categoriaGastoId: string;
  categoriaGasto: { id: string; nombre: string; tipo: string };
  proveedor: { id: string; nombre: string } | null;
  proveedorNombre?: string | null;
}

export interface Cuota {
  id: string;
  numero: number;
  descripcion: string;
  fechaEmisionEstimada: string;
  fechaCobroEstimada: string;
  montoNeto: number;
  montoIva: number;
  montoTotal: number;
  estado: string;
  hitoId: string | null;
  facturaId: string | null;
}

export interface FacturaProyecto {
  id: string;
  numero: string | null;
  folioSii: string | null;
  tipoDocumento: string;
  fechaEmision: string;
  fechaVencimiento: string;
  condicionPago: string;
  montoNeto: number;
  ivaPct: number;
  montoIva: number;
  montoTotal: number;
  estado: string;
  numeroOrdenCompra: string | null;
  notas: string | null;
  pagos: { id: string; fecha: string; monto: number; medio: string; referencia: string | null }[];
}

export const useProyecto = (id: string | undefined) =>
  useQuery({
    queryKey: [...claves.proyectos, id],
    queryFn: () => api<ProyectoDetalle>(`/proyectos/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// Cobranza
// ─────────────────────────────────────────────────────────────

export type Cartera = CarteraCobranzaRespuesta & {
  totalFacturado: number;
  totalCobrado: number;
  diasPromedioCobro: number | null;
  antiguedad?: { tramos: { clave: string; etiqueta: string; monto: number; cantidad: number }[]; totalPorCobrar: number; totalVencido: number };
  concentracionPorOrganismo?: { organismoId: string; nombre: string; saldo: number; facturas: number }[];
};

export const useCartera = () =>
  useQuery({
    queryKey: [...claves.facturas, 'cartera'],
    queryFn: () => api<Cartera>('/facturas/cartera'),
  });

export interface Factura {
  id: string;
  numero: string | null;
  tipoDocumento: string;
  fechaEmision: string;
  fechaVencimiento: string;
  condicionPago: string;
  montoNeto: number;
  montoIva: number;
  montoTotal: number;
  montoPagado?: number;
  saldoPendiente?: number;
  diasMora?: number;
  estaVencida?: boolean;
  situacionCobro?: string;
  numeroOrdenCompra?: string | null;
  textoCobroSugerido?: string;
  estado: string;
  organismoId?: string;
  organismo: { id: string; nombre: string; rut?: string | null };
  proyectoId?: string | null;
  proyecto: { id: string; codigo: string; nombre: string } | null;
  pagos: { id: string; fecha: string; monto: number; medio: string; referencia?: string | null }[];
  situacion: { estado: string; montoPagado: number; saldo: number; pagadoPct: number; diasAtraso: number; estaVencida: boolean };
}

export const useFacturas = (filtros: Record<string, string | boolean | number | undefined> = {}) =>
  useQuery({
    queryKey: [...claves.facturas, 'lista', filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [clave, valor] of Object.entries(filtros)) {
        if (valor !== undefined && valor !== '' && valor !== false) params.set(clave, String(valor));
      }
      return api<Pagina<Factura> & { totales: TotalesListadoFacturas }>(`/facturas?${params}`);
    },
  });

export const useTextoCobroFactura = (facturaId: string | undefined) =>
  useQuery({
    queryKey: [...claves.facturas, facturaId, 'texto-cobro'],
    queryFn: () =>
      api<{
        facturaId: string;
        numero: string | null;
        organismoNombre: string;
        numeroOrdenCompra: string | null;
        diasMora: number;
        montoTotal: number;
        saldoPendiente: number;
        textoCobro: string;
        whatsappUrl: string;
      }>(`/facturas/${facturaId}/texto-cobro`),
    enabled: Boolean(facturaId),
  });

export const useContactosFactura = (facturaId: string | undefined) =>
  useQuery({
    queryKey: [...claves.facturas, facturaId, 'contactos'],
    queryFn: () =>
      api<{
        organismoId: string;
        organismoNombre: string;
        contactos: {
          id: string;
          nombre: string;
          cargo: string | null;
          email: string | null;
          telefono: string | null;
          esPrincipal: boolean;
          whatsappUrl: string;
        }[];
      }>(`/facturas/${facturaId}/contactos`),
    enabled: Boolean(facturaId),
  });

/**
 * Registrar un pago toca cobranza, el proyecto y los KPIs a la vez. Invalidar
 * las tres ramas es más simple —y más difícil de equivocar— que actualizar cada
 * caché a mano.
 */
export const useRegistrarPago = () => {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { facturaId: string; fecha: string; monto: number; medio: string; referencia?: string }) =>
      api<Factura>(`/facturas/${datos.facturaId}/pagos`, { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.facturas });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
      void cliente.invalidateQueries({ queryKey: ['notificaciones'] });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Gastos
// ─────────────────────────────────────────────────────────────

export interface Gasto {
  id: string;
  ambito?: 'PROYECTO' | 'GENERAL';
  proyectoId?: string | null;
  fecha: string;
  concepto: string;
  montoNeto: number;
  ivaPct?: number;
  montoIva?: number;
  montoTotal: number;
  tipoDocumento?: string;
  numeroDocumento?: string | null;
  estadoPago: string;
  fechaPago?: string | null;
  notas?: string | null;
  categoriaGastoId?: string;
  categoriaGasto: { id: string; nombre: string; tipo: string };
  proyecto: { id: string; codigo: string; nombre: string } | null;
  proveedor: { id: string; nombre: string; rut?: string | null } | null;
  proveedorNombre?: string | null;
  proveedorRut?: string | null;
}

export const useConsolaCostos = (mes?: string) =>
  useQuery({
    queryKey: [...claves.gastos, 'consola', mes],
    queryFn: () => {
      const params = mes ? `?mes=${encodeURIComponent(mes)}` : '';
      return api<ConsolaCostosRespuesta>(`/gastos/consola${params}`);
    },
    staleTime: 60_000,
  });

export const useConsolaCostosServicios = (proyectoId?: string) =>
  useQuery({
    queryKey: [...claves.gastos, 'servicios', 'consola', proyectoId],
    queryFn: () => {
      const params = proyectoId ? `?proyectoId=${encodeURIComponent(proyectoId)}` : '';
      return api<ConsolaCostosServiciosRespuesta>(`/gastos/servicios/consola${params}`);
    },
    staleTime: 60_000,
  });

export const useGastos = (filtros: Record<string, string | boolean | number | undefined> = {}) =>
  useQuery({
    queryKey: [...claves.gastos, 'lista', filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [clave, valor] of Object.entries(filtros)) {
        if (valor !== undefined && valor !== '' && valor !== false) params.set(clave, String(valor));
      }
      return api<Pagina<Gasto> & { totales: TotalesLibroGastos }>(`/gastos?${params}`);
    },
  });

export const useCrearGasto = () => {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: Record<string, unknown>) => api<Gasto>('/gastos', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Catálogos
// ─────────────────────────────────────────────────────────────

export interface Categoria {
  id: string;
  nombre: string;
  color: string;
  margenObjetivoPct: number;
  activa: boolean;
}

export const useCategorias = () =>
  useQuery({
    queryKey: [...claves.catalogos, 'categorias'],
    queryFn: () => api<Categoria[]>('/categorias'),
    // Los catálogos cambian rara vez: no tiene sentido volver a pedirlos en
    // cada montaje de un formulario.
    staleTime: 10 * 60_000,
  });

export const useCategoriasGasto = () =>
  useQuery({
    queryKey: [...claves.catalogos, 'categorias-gasto'],
    queryFn: () => api<{ id: string; nombre: string; tipo: string }[]>('/categorias-gasto'),
    staleTime: 10 * 60_000,
  });

export interface ContactoOrganismo {
  id: string;
  organismoId: string;
  nombre: string;
  cargo: string | null;
  email: string | null;
  telefono: string | null;
  notas: string | null;
  creadoEn: string;
}

export interface Organismo {
  id: string;
  nombre: string;
  rut: string | null;
  tipo: string;
  region: string | null;
  comuna: string | null;
  direccion: string | null;
  sitioWeb: string | null;
  notas: string | null;
  totalLicitaciones: number;
  adjudicadas: number;
  perdidas: number;
  tasaExitoPct: number | null;
  montoGanado: number;
  diasPromedioPago: number | null;
  sobreplazoDias: number | null;
  facturasPagadas: number;
  proyectosActivos: number;
  montoPendiente: number;
  montoMora60d: number;
  contactos?: ContactoOrganismo[];
  _count?: { licitaciones: number; contactos: number };
}

export interface OrganismoDetalle extends Organismo {
  contactos: ContactoOrganismo[];
  licitaciones: {
    id: string;
    codigoMercadoPublico: string;
    titulo: string;
    estado: string;
    montoPresupuestoNeto: number;
    montoAdjudicadoNeto: number;
    fechaCierrePostulacion: string | null;
    categoria?: { nombre: string; color: string } | null;
  }[];
  proyectos: {
    id: string;
    codigo: string;
    nombre: string;
    estado: string;
    montoContratoNeto: number;
    montoContratoBruto: number;
    fechaInicio: string;
    fechaFinPlanificada: string | null;
    _count?: { hitos: number };
  }[];
  facturas: {
    id: string;
    numero: string | null;
    folioSii: string | null;
    tipoDocumento: string;
    fechaEmision: string;
    condicionPago: string;
    fechaVencimiento: string;
    montoNeto: number;
    montoIva: number;
    montoTotal: number;
    estado: string;
    proyecto?: { id: string; nombre: string } | null;
    pagos: { id: string; fecha: string; monto: number; medio: string; referencia: string | null }[];
    situacion?: {
      estado: string;
      montoPagado: number;
      saldo: number;
      pagadoPct: number;
      diasAtraso: number;
      estaVencida: boolean;
      diasHastaPagoCompleto: number | null;
    };
  }[];
  metricas: {
    totalLicitaciones: number;
    adjudicadas: number;
    perdidas: number;
    tasaExitoPct: number | null;
    montoGanado: number;
    ticketPromedio: number | null;
    proyectosActivos: number;
    totalFacturado: number;
    totalCobrado: number;
    saldoPendiente: number;
    mora60d: number;
    diasPromedioPago: number | null;
    sobreplazoDias: number | null;
    facturasPagadas: number;
  };
}

export const useOrganismos = (busqueda?: string) =>
  useQuery({
    queryKey: [...claves.organismos, busqueda],
    queryFn: () => api<Organismo[]>(`/organismos${busqueda ? `?busqueda=${encodeURIComponent(busqueda)}` : ''}`),
  });

export const useOrganismo = (id?: string) =>
  useQuery({
    queryKey: [...claves.organismos, id],
    queryFn: () => api<OrganismoDetalle>(`/organismos/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// Predicción
// ─────────────────────────────────────────────────────────────

export interface PuntoCurva {
  precio: number;
  precioNeto: number;
  precioBruto: number;
  ratio: number;
  probabilidadPct: number;
  utilidadEsperada: number | null;
}

export interface Prediccion {
  precio: {
    precioSugerido: number;
    precioSugeridoNeto?: number;
    precioSugeridoBruto?: number;
    rangoMin: number;
    rangoMinNeto?: number;
    rangoMinBruto?: number;
    rangoMax: number;
    rangoMaxNeto?: number;
    rangoMaxBruto?: number;
    ratioMercado: number;
    evidencia: { nivel: string; n: number; nEfectivo: number; detalle: string };
    pisoPorMargen: number | null;
    pisoPorMargenNeto?: number | null;
    pisoPorMargenBruto?: number | null;
    margenEstimadoPct: number | null;
    advertencias: string[];
    curvaProbabilidad: PuntoCurva[];
    probabilidadEnSugerido: number | null;
  };
  plazo: {
    plazoSugeridoDias: number | null;
    rangoMinDias: number | null;
    rangoMaxDias: number | null;
    evidencia: { nivel: string; n: number; nEfectivo: number; detalle: string };
    advertencias: string[];
    limitadoPorBases: boolean;
  };
  modeloVersion: string;
}

export interface EntradaPrediccion {
  categoriaId: string;
  presupuestoOficialNeto: number;
  presupuestoOficialBruto?: number;
  modalidadMonto?: 'NETO' | 'BRUTO';
  organismoId?: string | null;
  tipoOrganismo?: string | null;
  tipoProceso?: string | null;
  plazoMaximoBasesDias?: number | null;
  cantidadOferentesEsperada?: number | null;
}

/**
 * La predicción se pide mientras el usuario escribe, así que se cachea por
 * entrada exacta y no se reintenta: un error acá no debe llenar la consola ni
 * castigar al servidor con reintentos por cada tecla.
 */
export const usePrediccion = (entrada: EntradaPrediccion | null) =>
  useQuery({
    queryKey: [...claves.prediccion, entrada],
    queryFn: () => api<Prediccion>('/prediccion', { method: 'POST', body: entrada }),
    enabled: Boolean(entrada && entrada.categoriaId && entrada.presupuestoOficialNeto > 0),
    retry: false,
    staleTime: 60_000,
  });

export interface DiagnosticoPredictor {
  totalHistorico: number;
  utilizablesParaPrecio: number;
  sinPresupuestoOficial: number;
  sinMontoGanador: number;
  sinPlazoRegistrado: number;
  proyectosConCostos: number;
  porCategoria: { categoriaId: string; nombre: string; observaciones: number }[];
  recomendaciones: string[];
}

export const useDiagnosticoPredictor = () =>
  useQuery({
    queryKey: [...claves.prediccion, 'diagnostico'],
    queryFn: () => api<DiagnosticoPredictor>('/prediccion/diagnostico'),
  });

// ─────────────────────────────────────────────────────────────
// Motor: parámetros y validación
// ─────────────────────────────────────────────────────────────

export interface ParametrosPredictor {
  vidaMediaMeses: number;
  agresividadPrecio: number;
  agresividadPlazo: number;
  pesoMismoOrganismo: number;
  pesoMismoTipoOrganismo: number;
  pesoMismoTipoProceso: number;
  winsorPct: number;
  margenMinimoPct: number;
  plazoMinimoDias: number;
}

export const useParametrosPredictor = () =>
  useQuery({
    queryKey: [...claves.prediccion, 'parametros'],
    queryFn: () => api<ParametrosPredictor>('/prediccion/parametros'),
  });

export interface CasoBacktest {
  licitacionId: string;
  codigo: string;
  nombre: string;
  fecha: string | null;
  precioRecomendado: number;
  precioReal: number;
  errorPrecio: number;
  errorPrecioPct: number;
  plazoRecomendado: number | null;
  plazoReal: number | null;
  errorPlazoDias: number | null;
  observacionesDisponibles: number;
  habriaGanado: boolean;
}

export interface ResultadoBacktest {
  casos: CasoBacktest[];
  mapePrecio: number | null;
  sesgoPrecioPct: number | null;
  errorMedioPlazoDias: number | null;
  tasaAciertoPct: number | null;
  casosEvaluados: number;
  casosDescartados: number;
  interpretacion: string;
}

/** Cuando se envía una configuración alternativa, la API compara las dos. */
export interface ComparacionBacktest {
  a: ResultadoBacktest;
  b: ResultadoBacktest;
  mejora: string;
}

export interface BrechaPlazo {
  id: string;
  nombre: string;
  color: string;
  brechaPromedio: number;
  n: number;
}

export const useBrechasDePlazo = () =>
  useQuery({
    queryKey: [...claves.prediccion, 'brechas-plazo'],
    queryFn: () => api<BrechaPlazo[]>('/prediccion/brechas-plazo'),
  });

export const useConsolaMotor = () =>
  useQuery({
    queryKey: [...claves.prediccion, 'consola'],
    queryFn: () => api<ConsolaMotorRespuesta>('/prediccion/consola'),
  });

export const useValidacionHistorica = (filtros?: { categoriaId?: string; minimoObservaciones?: number }) =>
  useQuery({
    queryKey: [...claves.prediccion, 'validacion-historica', filtros],
    queryFn: () => {
      const search = new URLSearchParams();
      if (filtros?.categoriaId) search.set('categoriaId', filtros.categoriaId);
      if (filtros?.minimoObservaciones !== undefined) search.set('minimoObservaciones', String(filtros.minimoObservaciones));
      const query = search.toString();
      return api<ValidacionHistoricaRespuesta>(`/prediccion/validacion-historica${query ? `?${query}` : ''}`);
    },
  });

// ─────────────────────────────────────────────────────────────
// Configuración de la Empresa y del Sistema
// ─────────────────────────────────────────────────────────────

export const useConfiguracionEmpresa = () =>
  useQuery({
    queryKey: [...claves.configuracion, 'perfil'],
    queryFn: () => api<ConfiguracionEmpresaDto>('/configuracion'),
  });

export const useCategoriasComercialesConfig = (incluirInactivas = true) =>
  useQuery({
    queryKey: [...claves.configuracion, 'categorias', { incluirInactivas }],
    queryFn: () => api<Categoria[]>(`/configuracion/categorias?incluirInactivas=${incluirInactivas}`),
  });

export const useCategoriasGastoConfig = (incluirInactivas = true) =>
  useQuery({
    queryKey: [...claves.configuracion, 'categorias-gasto', { incluirInactivas }],
    queryFn: () =>
      api<{ id: string; nombre: string; tipo: string; esCostoPersonal?: boolean; orden?: number; activa?: boolean }[]>(
        `/configuracion/categorias-gasto?incluirInactivas=${incluirInactivas}`,
      ),
  });

export const useParametrosPredictorConfig = () =>
  useQuery({
    queryKey: [...claves.configuracion, 'predictor'],
    queryFn: () => api<ParametrosPredictorDto>('/configuracion/predictor'),
  });

export const useUsuariosEmpresa = () =>
  useQuery({
    queryKey: [...claves.configuracion, 'usuarios'],
    queryFn: () => api<UsuarioEmpresaDto[]>('/configuracion/usuarios'),
  });

export const useAuditoriaConfiguracion = (limite = 50) =>
  useQuery({
    queryKey: [...claves.configuracion, 'auditoria', { limite }],
    queryFn: () => api<ItemAuditoriaConfiguracionDto[]>(`/configuracion/auditoria?limite=${limite}`),
  });

// ─────────────────────────────────────────────────────────────
// Competencia & Inteligencia de Mercado (Módulo 3)
// ─────────────────────────────────────────────────────────────

export interface KpisCompetencia {
  winRateCaraACara: number;
  brechaPrecioPromedioPct: number | null;
  dineroPerdido: number;
  causaPrincipalDerrota: string;
  totalEnfrentamientos: number;
  totalGanadas: number;
  totalPerdidas: number;
  distribucionCausas: { motivo: string; cantidad: number; porcentaje: number }[];
}

export interface MetricaSectorCompetencia {
  sector: TipoOrganismo;
  totalProcesos: number;
  ganadas: number;
  perdidas: number;
  winRatePct: number;
  brechaPrecioPromedioPct: number | null;
  montoMercadoNeto: number;
  rivalDominante: { id: string; nombre: string; victorias: number } | null;
}

export interface BancoPreciosGanadores {
  promedioRatioAdjudicacionPct: number | null;
  rangoMinimoPct: number | null;
  rangoMaximoPct: number | null;
  totalMuestra: number;
  porSector: { sector: string; ratioPromedioPct: number; muestraTotal: number }[];
  porCategoria: { categoriaId: string; categoriaNombre: string; ratioPromedioPct: number; muestraTotal: number }[];
}

export interface CompetidorDirectorio {
  id: string;
  nombre: string;
  rut: string | null;
  sitioWeb: string | null;
  notas: string | null;
  totalOfertas: number;
  victorias: number;
  montoGanado: number;
  caraACara: {
    enfrentamientosTotales: number;
    ganadasPorNosotros: number;
    ganadasPorRival: number;
    winRateVsRivalPct: number;
  };
  brechaPrecioPromedioPct: number | null;
  ratioPresupuestoPromedioPct: number | null;
  perfilEstrategico: PerfilCompetidor;
  perfilEtiqueta: string;
  terrenoFuerte: { id: string; nombre: string; victorias: number }[];
}

export interface TableroCompetencia {
  kpis: KpisCompetencia;
  metricasPorSector: MetricaSectorCompetencia[];
  bancoPreciosGanadores: BancoPreciosGanadores;
  directorio: CompetidorDirectorio[];
}

export const useTableroCompetencia = (filtros?: FiltrosCompetencia) =>
  useQuery({
    queryKey: [...claves.competencia, 'tablero', filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filtros?.sector) params.set('sector', filtros.sector);
      if (filtros?.categoriaId) params.set('categoriaId', filtros.categoriaId);
      if (filtros?.busqueda) params.set('busqueda', filtros.busqueda);
      if (filtros?.ordenarPor) params.set('ordenarPor', filtros.ordenarPor);
      const query = params.toString();
      return api<TableroCompetencia>(`/competencia${query ? `?${query}` : ''}`);
    },
  });

export interface HeadToHeadLicitacion {
  licitacionId: string;
  codigoMercadoPublico: string;
  nombreLicitacion: string;
  organismo: { id: string; nombre: string; tipo: TipoOrganismo; comuna?: string | null };
  categoria: { id: string; nombre: string; color?: string | null };
  fecha: string;
  presupuestoOficialNeto: number;
  nuestraOferta: {
    montoNeto: number;
    plazoDias: number;
  };
  ofertaRival: {
    montoNeto: number;
    plazoDias: number | null;
    puntaje: number | null;
    esGanador: boolean;
  };
  resultado: 'GANADA_POR_NOSOTROS' | 'GANADA_POR_RIVAL' | 'GANADA_POR_TERCERO' | 'EN_CURSO';
  brechaPrecioPct: number | null;
  brechaPlazoDias: number | null;
}

export interface ResumenCaraACara {
  totalEnfrentamientos: number;
  ganadasPorNosotros: number;
  ganadasPorRival: number;
  ganadasPorTerceros: number;
  winRateVsRivalPct: number;
  brechaPrecioPromedioPct: number | null;
  brechaPlazoPromedioDias: number | null;
  montoTotalDisputa: number;
  montoGanadoPorRival: number;
}

export interface HeadToHeadDetalle {
  competidor: {
    id: string;
    nombre: string;
    rut: string | null;
    sitioWeb: string | null;
    notas: string | null;
    perfilEstrategico: PerfilCompetidor;
    perfilEtiqueta: string;
  };
  resumenCaraACara: ResumenCaraACara;
  historial: HeadToHeadLicitacion[];
}

export const useHeadToHead = (competidorId: string | null) =>
  useQuery({
    queryKey: [...claves.competencia, 'head-to-head', competidorId],
    queryFn: () => api<HeadToHeadDetalle>(`/competencia/head-to-head/${competidorId}`),
    enabled: !!competidorId,
  });

export interface SimulacionPrecioResultado {
  presupuestoOficialNeto: number;
  competidor: { id: string; nombre: string } | null;
  perfilEstrategico: PerfilCompetidor;
  perfilEtiqueta: string;
  ratioAplicadoPct: number;
  ofertaProbableRivalNeto: number;
  pisoMinimoAceptableNeto: number | null;
  ofertaRecomendadaNeto: number;
  margenProyectadoPct: number | null;
  probabilidadGanarPct: number;
  alerta: string | null;
  estrategiaSugerida: string;
}

export const useSimularPrecio = (params: SimularOfertaCompetidorInput | null) =>
  useQuery({
    queryKey: [...claves.competencia, 'simular', params],
    queryFn: () => {
      if (!params) throw new Error('Parámetros requeridos para simular');
      const search = new URLSearchParams();
      search.set('presupuestoOficialNeto', String(params.presupuestoOficialNeto));
      if (params.competidorId) search.set('competidorId', params.competidorId);
      if (params.perfil) search.set('perfil', params.perfil);
      if (params.costoDirectoNeto !== undefined && params.costoDirectoNeto > 0) {
        search.set('costoDirectoNeto', String(params.costoDirectoNeto));
      }
      if (params.margenMinimoPct !== undefined) {
        search.set('margenMinimoPct', String(params.margenMinimoPct));
      }
      return api<SimulacionPrecioResultado>(`/competencia/simular?${search.toString()}`);
    },
    enabled: !!params && params.presupuestoOficialNeto > 0,
  });

export interface CompetidorCatalogo {
  id: string;
  nombre: string;
  rut: string | null;
  sitioWeb: string | null;
  notas: string | null;
  totalOfertas: number;
  victorias: number;
  montoGanadoNeto: number;
}

export const useCompetidoresCatalogo = (busqueda?: string) =>
  useQuery({
    queryKey: [...claves.competencia, 'competidores', { busqueda }],
    queryFn: () => {
      const search = busqueda ? `?busqueda=${encodeURIComponent(busqueda)}` : '';
      return api<CompetidorCatalogo[]>(`/competencia/competidores${search}`);
    },
  });

// ─────────────────────────────────────────────────────────────
// Gastos de Empresa & RRHH / Remuneraciones (Módulo B)
// ─────────────────────────────────────────────────────────────

export const useConsolaRrhh = (mes?: string) =>
  useQuery({
    queryKey: [...claves.rrhh, 'consola', mes],
    queryFn: () => {
      const params = mes ? `?mes=${encodeURIComponent(mes)}` : '';
      return api<ConsolaRrhhGastosEmpresaRespuesta>(`/rrhh/consola${params}`);
    },
    staleTime: 30_000,
  });

export const useRemuneraciones = (filtros: Partial<FiltrosRemuneracionInput> = {}) =>
  useQuery({
    queryKey: [...claves.rrhh, 'remuneraciones', filtros],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filtros.periodoMes) params.set('periodoMes', filtros.periodoMes);
      if (filtros.estadoPago && filtros.estadoPago !== 'TODOS') params.set('estadoPago', filtros.estadoPago);
      if (filtros.busqueda) params.set('busqueda', filtros.busqueda);
      if (filtros.prevision) params.set('prevision', filtros.prevision);
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      return api<RespuestaPaginada<RemuneracionItem>>(`/rrhh/remuneraciones${queryStr}`);
    },
  });

export const usePlanillaPrevired = (mes?: string) =>
  useQuery({
    queryKey: [...claves.rrhh, 'previred', mes],
    queryFn: () => {
      const params = mes ? `?mes=${encodeURIComponent(mes)}` : '';
      return api<PlanillaPreviredRespuesta>(`/rrhh/previred${params}`);
    },
    staleTime: 60_000,
  });


